import { h } from "preact";
import { useState, useRef, useCallback, useEffect } from "preact/hooks";
import { MAX_MESSAGE_CHARS, tooLongMessage } from "../../../../shared/util/messageLimits";

interface ChatInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  messageCount?: number;
  messageLimit?: number;
}

/** SVG send arrow icon */
function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

export function ChatInput({ onSend, disabled, messageCount, messageLimit }: ChatInputProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // The server refuses longer messages; saying so here, live, means a pasted data
  // file is caught before it is sent rather than bounced after.
  const tooLong = value.length > MAX_MESSAGE_CHARS;

  // Focus the input when the panel opens, and re-focus when it re-enables
  // after a response finishes streaming (standard chat UX). Deferred to the
  // next frame so it wins the focus race against the streaming re-renders and
  // the auto-scroll that fire on the same tick when a response completes.
  useEffect(() => {
    if (!disabled) {
      const id = requestAnimationFrame(() => textareaRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [disabled]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled || trimmed.length > MAX_MESSAGE_CHARS) return;
    onSend(trimmed);
    setValue("");
    // Reset textarea height and keep focus in the input (covers the case where
    // the user clicked the Send button, which would otherwise hold focus).
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.focus();
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Stop ALL key events from bubbling to the host page.
      // MkDocs Material (and other sites) bind keyboard shortcuts
      // (s/f/? open search, arrow keys navigate) that steal focus.
      e.stopPropagation();
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 100) + "px";
  }, []);

  const showCounter = messageLimit != null && messageLimit > 0;

  return (
    <div class="widget-input-area-wrapper">
      {showCounter && (
        <div class="widget-message-counter">
          {messageCount ?? 0} / {messageLimit} messages used
        </div>
      )}
      {tooLong && (
        <div class="widget-too-long" role="alert">
          {tooLongMessage(value.length)}
        </div>
      )}
      <div class="widget-input-area">
      <textarea
        ref={textareaRef}
        class="widget-textarea"
        // Enter sends. Students typed "How do I edit this code:", pressed Enter, and sent
        // the question without the code; the placeholder says how to get a new line.
        placeholder="Ask a question… (Shift+Enter for a new line)"
        value={value}
        onInput={(e) => {
          setValue((e.target as HTMLTextAreaElement).value);
          handleInput();
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={(e: KeyboardEvent) => e.stopPropagation()}
        onKeyPress={(e: KeyboardEvent) => e.stopPropagation()}
        disabled={disabled}
        rows={1}
      />
      <button
        class="widget-send-btn"
        onClick={handleSend}
        disabled={disabled || !value.trim() || tooLong}
        title="Send message"
      >
        <SendIcon />
      </button>
      </div>
    </div>
  );
}
